import { ncsStandardTemperatureC } from '../data/emgClinicalCriteria';
import type { ClinicalReport } from './jsonReportGenerator';

export type EdxQualitySeverity = 'error' | 'warning' | 'info';

export interface EdxReportQualityIssue {
  id: string;
  severity: EdxQualitySeverity;
  title: string;
  message: string;
  section: 'metadata' | 'ncs' | 'emg' | 'special_studies' | 'conclusion';
}

const isBlank = (value?: string | null): boolean => !value || value.trim().length === 0;

export function evaluateEdxReportQuality(report: ClinicalReport): EdxReportQualityIssue[] {
  const issues: EdxReportQualityIssue[] = [];
  const ncsResults = report.electrophysiologicalData.ncs?.results || [];
  const emg = report.electrophysiologicalData.emg;

  if (isBlank(report.metadata.limitations.description)) {
    issues.push({
      id: 'missing-limitations',
      severity: 'warning',
      title: 'Limitaciones no documentadas',
      message: 'Documente “sin limitaciones” o especifique dolor, edema, falta de cooperación u otra restricción técnica.',
      section: 'metadata',
    });
  }

  if (report.metadata.needleElectrode === 'not_specified' && emg?.status === 'performed') {
    issues.push({
      id: 'missing-needle-electrode',
      severity: 'info',
      title: 'Tipo de aguja no especificado',
      message: 'Registre si se usó electrodo concéntrico o monopolar para mejorar trazabilidad técnica.',
      section: 'metadata',
    });
  }

  ncsResults.forEach(result => {
    if (result.temperature === undefined || result.temperature === null) {
      issues.push({
        id: `missing-temperature-${result.id}`,
        severity: 'warning',
        title: 'Temperatura NCS ausente',
        message: `${result.nerve} ${result.side}: registre temperatura cutánea para interpretar latencias y velocidades con seguridad.`,
        section: 'ncs',
      });
    } else if (result.temperature < ncsStandardTemperatureC(result.nerve)) {
      issues.push({
        id: `low-temperature-${result.id}`,
        severity: 'warning',
        title: 'Temperatura bajo estándar',
        message: `${result.nerve} ${result.side}: ${result.temperature} °C está bajo el mínimo esperado (${ncsStandardTemperatureC(result.nerve)} °C). Considere calentamiento o interpretación cautelosa.`,
        section: 'ncs',
      });
    }

    if (result.status === 'abnormal' && !result.referenceValues) {
      issues.push({
        id: `missing-reference-${result.id}`,
        severity: 'warning',
        title: 'Anormalidad sin valores de referencia',
        message: `${result.nerve} ${result.side}: el registro está marcado como anormal, pero no incluye los límites de referencia usados.`,
        section: 'ncs',
      });
    }

    if (result.status === 'abnormal' && (!result.findings || result.findings.length === 0)) {
      issues.push({
        id: `missing-finding-detail-${result.id}`,
        severity: 'info',
        title: 'Anormalidad poco descrita',
        message: `${result.nerve} ${result.side}: agregue una interpretación breve del parámetro alterado.`,
        section: 'ncs',
      });
    }
  });

  if (!emg || emg.status === 'nodata') {
    issues.push({
      id: 'missing-emg-data',
      severity: 'info',
      title: 'EMG sin datos',
      message: 'Si la aguja no fue realizada o fue limitada, documente la razón para que la conclusión sea trazable.',
      section: 'emg',
    });
  } else if (emg.status === 'performed') {
    if (emg.muscles.length === 0) {
      issues.push({
        id: 'emg-no-muscles',
        severity: 'error',
        title: 'EMG realizada sin músculos listados',
        message: 'Un reporte profesional debe listar cada músculo examinado de forma individual.',
        section: 'emg',
      });
    } else if (emg.muscles.length < 4 && report.conclusion.primaryDiagnosis.toLowerCase().includes('radiculopat')) {
      issues.push({
        id: 'radiculopathy-low-sampling',
        severity: 'warning',
        title: 'Muestreo EMG limitado para radiculopatía',
        message: 'Para sospecha radicular, documente muestreo suficiente por miotomas/nervios o explique la limitación.',
        section: 'emg',
      });
    }

    const missingVoluntaryDetail = emg.muscles.filter(muscle =>
      muscle.recruitmentPattern === 'no especificado' ||
      muscle.mupAmplitude === 0 ||
      muscle.mupDuration === 0
    );
    if (missingVoluntaryDetail.length > 0) {
      issues.push({
        id: 'emg-voluntary-detail',
        severity: 'info',
        title: 'Actividad voluntaria incompleta',
        message: `${missingVoluntaryDetail.length} músculo(s) no tienen morfología PUM o reclutamiento completamente documentado.`,
        section: 'emg',
      });
    }
  }

  if (report.conclusion.confidence < 0.5) {
    issues.push({
      id: 'low-confidence-conclusion',
      severity: 'info',
      title: 'Conclusión de baja confianza',
      message: 'La impresión diagnóstica tiene confianza baja; considere reforzar datos de soporte, limitaciones o diferencial.',
      section: 'conclusion',
    });
  }

  return issues;
}
